import { IconButton } from '@/components/admin/reglages/reference-list';
import type { LinkFormRow } from '@/lib/link-form';
import { linkEntryProblemSentence } from '@/lib/link-labels';
import type { ExperienceCard, LinkEntryProblem } from '@matter_extractor/shared';
import { Icon, Select } from '@pms-digital/design-system';

/**
 * One row of the entries block of a link made on the VITRINE (docs/specs/vitrine.md
 * section 3.4, V5, issue #192): an experience and a trash - and the refusal
 * under the row. NO version and NO tag: an entry of the vitrine always plays the
 * latest version in production of its experience, so there is nothing to choose.
 *
 * It is the row of `link-entry-row.tsx` without its second select, and a file of
 * its own rather than a branch of that one because the atelier's row is being
 * reworked (references `latest` or pinned, and a side): the vitrine's has none
 * of it and must not move with it. The `data-testid`s are the atelier's, so the
 * captures and the specs read both screens the same way.
 *
 * Only the experiences that have a version in production are offered
 * (`offered`, computed by the form): the one a link already names stays in the
 * list even when it no longer qualifies, so a « Modifier » never shows an empty
 * select where the operator once put something.
 */

export interface VitrineLinkEntryRowProps {
  readonly row: LinkFormRow;
  readonly index: number;
  /** The experiences this row may choose from, already narrowed to those in production. */
  readonly offered: readonly ExperienceCard[];
  readonly problem: LinkEntryProblem | null;
  readonly removable: boolean;
  readonly disabled: boolean;
  readonly onChange: (patch: Partial<LinkFormRow>) => void;
  readonly onRemove: () => void;
}

export function VitrineLinkEntryRow({
  row,
  index,
  offered,
  problem,
  removable,
  disabled,
  onChange,
  onRemove,
}: VitrineLinkEntryRowProps) {
  const refused = problem !== null;

  return (
    <li data-testid="partage-entree" className="space-y-2">
      <div className="flex items-end gap-3">
        {/* The package `Select` (Radix): its mark is on this wrapper. « Choisir une
            expérience » is its placeholder, shown while the row names none - no longer an
            option one could pick back. A refusal reddens it; its sentence stays under the row. */}
        <div data-testid="partage-entree-experience" className="min-w-0 flex-1">
          <Select
            label="Expérience"
            value={row.experienceId}
            placeholder="Choisir une expérience"
            disabled={disabled}
            status={refused ? 'error' : 'default'}
            options={offered.map((experience) => ({
              value: experience.id,
              label:
                experience.retiredAt === null ? experience.name : `${experience.name} (retirée)`,
            }))}
            onValueChange={(experienceId) => onChange({ experienceId })}
          />
        </div>

        {/* Kept mounted and DISABLED on the last row, as on the atelier: §5.1
            forbids an entry-less link, and a control that vanishes under the
            cursor is one an operator hunts for. */}
        <IconButton
          label={`Retirer l’entrée ${index + 1}`}
          tone="danger"
          testId="partage-entree-retirer"
          disabled={disabled || !removable}
          onClick={onRemove}
        >
          <Icon name="delete" size={16} />
        </IconButton>
      </div>

      {refused ? (
        <p data-testid="partage-entree-refus" className="text-rouge-deep text-small">
          {linkEntryProblemSentence(problem)}
        </p>
      ) : null}
    </li>
  );
}
